import React, { useState, useEffect } from "react";
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { useAxiosAuth } from "./api/apiAuth";
import useAxiosInterceptors from './api/axiosInterceptors';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import { UserProvider } from './context/UserContext';

import LoginPage from "./Login/LoginPage";
import Home from "./components/home/home";
import Logout from "./components/logout";
import Loader from './components/Loader';

import ErrorAlert from "./utils/errorAlert";
import { msalInstance } from "./msalConfig";
import {CryptoUtility} from './utils/cryptoUtility.js';
import powerBISettings from './configs/powerBISettings';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    // You can log error info here
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: 'red', padding: 20 }}><h2>Something went wrong.</h2><pre>{this.state.error && this.state.error.toString()}</pre></div>;
    }
    return this.props.children;
  }
}

function useAppIsAuthenticated() {
  const msalAuthenticated = useIsAuthenticated();
  const jwtAuthenticated = localStorage.getItem('jwt_authenticated') === 'true';
  return jwtAuthenticated ? true : msalAuthenticated;
}

function AppContent({ notificationPanelOpen, setNotificationPanelOpen }) {  
  const [errorMessage, setErrorMessage] = useState("");  
  const [errorDetail, setErrorDetail] = useState("");  
  const [logoutOnClose, setLogoutOnClose] = useState(false);  
  useAxiosInterceptors({ setErrorMessage, setErrorDetail, setLogoutOnClose });  
  const isAuthenticated = useAppIsAuthenticated();  
  const { loading } = useLoading();  
  const [isHvaRoute, setIsHvaRoute] = useState(false);  
  const [hasParams, setHasParams] = useState(false);
  
  // Check if current route is /hva and if there are query parameters
  useEffect(() => {  
    setIsHvaRoute(window.location.pathname === '/hva');  
    setHasParams(!!window.location.search && window.location.search !== '');
  }, []);  
  
  const handleCloseError = () => {  
    setErrorMessage("");  
    setErrorDetail("");  
    if (logoutOnClose) {  
      localStorage.clear();  
      sessionStorage.clear();  
      if (msalInstance) {  
        msalInstance.logoutRedirect();  
      }  
      else {  
        window.location.reload();  
      }  
    }  
    setLogoutOnClose(false);  
  };  
  
  return (  
    <>  
      {loading && <Loader />}  
      <ErrorAlert  
        open={!!errorMessage}  
        errorMessage={errorMessage}  
        errorDetail={errorDetail}  
        onClose={handleCloseError}  
        logoutOnClose={logoutOnClose}  
      />  
      <Router>  
        <Routes>  
          {isAuthenticated ? (  
            <>  
              <Route path="/" element={<Home setNotificationPanelOpen={setNotificationPanelOpen} />} />  
              <Route path="/report" element={<Home setNotificationPanelOpen={setNotificationPanelOpen} />} />  
              <Route path="/logout" element={<Logout />} />  
              <Route path="*" element={<Navigate to="/report" />} />
 
            </>  
          ) : (  
            <>  
              {isHvaRoute && !hasParams ? (  
                <Route path="/hva" element={<LoginPage forceLogin={true} />} />  
              ) : (  
                <>  
                  <Route path="/hva" element={<LoginPage />} />  
                  <Route path="*" element={<LoginPage />} />  
                </>  
              )}  
            </>  
          )}  
        </Routes>  
      </Router>  
    </>  
  );  
}

function getQueryParams() {
  const params = {};
  // Support both hash and search for query params
  let queryString = window.location.search;
  if (!queryString || queryString === '') {
    // If no search, try hash (for /#/hva?param=...)
    const hash = window.location.hash;
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex !== -1) {
      queryString = hash.substring(hashQueryIndex);
    }
  }
  queryString.replace(/^[?#]/, '').split('&').forEach(pair => {
    // Only split on the first '=' to preserve '=' in values (e.g., base64 padding)
    const eqIdx = pair.indexOf('=');
    if (eqIdx !== -1) {
      const key = pair.substring(0, eqIdx);
      const value = pair.substring(eqIdx + 1);
      if (key) params[key] = decodeURIComponent(value || '');
    } else if (pair) {
      params[pair] = '';
    }
  });
  return params;
}

function decodeJwtUsername(token) {
  try {
    const jwtTokenSplitted = CryptoUtility.getDecryptedText(token, powerBISettings).split('.');
    const payload = jwtTokenSplitted[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.username || decoded.upn || decoded.email || '';
  } catch (err) {
    console.log('decodeJwtUsername error:', err);
    return '';
  }
}

function App() {
  useAxiosAuth();
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const params = getQueryParams();
      if (params.shva) {
        console.log('SHVA parameter found in URL. Updating localStorage.');
        const currentShva = localStorage.getItem('shva');
        const newShva = decodeURIComponent(params.shva);
        if (currentShva !== newShva) {
          console.log('Updating SHVA in localStorage.');
          localStorage.setItem('shva', newShva);
        }
      }
      else{
        console.log('No SHVA parameter in URL.');
      }

    };
    window.addEventListener('hashchange', handleHashChange);
    // Run once on mount
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    // JWT/CustomIDP logic
    const params = getQueryParams();
    const hasJwtParams = params.jwttoken && params.customidp;
    if (params.jwttoken && params.customidp) {
      localStorage.setItem('jwttoken', (params.jwttoken || '').replace(/ /g, '+'));
      localStorage.setItem('customidp', params.customidp);
      localStorage.setItem('jwt_authenticated', 'true');
      const username = decodeJwtUsername((params.jwttoken || '').replace(/ /g, '+'));
      localStorage.setItem('jwt_username', username);
      sessionStorage.setItem('firstVisit', 'true');
    } else {
      localStorage.removeItem('jwt_authenticated');
      localStorage.removeItem('jwttoken');
      localStorage.removeItem('customidp');
      localStorage.removeItem('jwt_username');
    }
    // Only redirect on first visit if NOT using JWT authentication  
    if (!sessionStorage.getItem('firstVisit') && !hasJwtParams) {  
      const logoutRedirectUrl = process.env.REACT_APP_LOGOUT_REDIRECT_URL;  
      if (logoutRedirectUrl && !window.location.href.includes(logoutRedirectUrl)) {  
        window.location.href = logoutRedirectUrl;  
      }  
      sessionStorage.setItem('firstVisit', 'true');  
    }  
    setAuthReady(true);
  }, []);

  if (!authReady) {
    return <Loader />;
  }

  

  return (
    <ErrorBoundary>
      <UserProvider>
        <LoadingProvider>
          <AppContent notificationPanelOpen={notificationPanelOpen} setNotificationPanelOpen={setNotificationPanelOpen} />
        </LoadingProvider>
      </UserProvider>
    </ErrorBoundary>
  );
}

export default App;
